import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export interface ShopifyIntegrationStatus {
  shop_domain: string;
  scope?: string | null;
  token_expires_at?: string | null;
  utmScriptInstalled?: boolean;
  utmScriptInstalledAt?: string | null;
}

interface UseShopifyIntegrationStatusResult {
  integration: ShopifyIntegrationStatus | null;
  loading: boolean;
  error: string | null;
  refresh: (options?: { silent?: boolean; force?: boolean }) => Promise<void>;
}

const getToken = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data?.session?.access_token) {
    throw new Error("Sessão inválida ou expirada.");
  }
  return data.session.access_token;
};

let cachedIntegration: ShopifyIntegrationStatus | null = null;
const listeners = new Set<(data: ShopifyIntegrationStatus | null) => void>();

const broadcast = (data: ShopifyIntegrationStatus | null) => {
  listeners.forEach((listener) => listener(data));
};

export const useShopifyIntegrationStatus = (): UseShopifyIntegrationStatusResult => {
  const [integration, setIntegration] = useState<ShopifyIntegrationStatus | null>(cachedIntegration);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(
    async (options?: { silent?: boolean; force?: boolean }) => {
      if (!options?.force && cachedIntegration) {
        setIntegration(cachedIntegration);
        return;
      }

      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch("/api/shopify/custom/status", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Falha ao carregar status.");
        if (json?.connected && json?.shopDomain) {
          const data: ShopifyIntegrationStatus = {
            shop_domain: json.shopDomain,
            scope: json.scope ?? null,
            token_expires_at: json.tokenExpiresAt ?? null,
            utmScriptInstalled: json.utmScriptInstalled ?? false,
            utmScriptInstalledAt: json.utmScriptInstalledAt ?? null,
          };
          cachedIntegration = data;
          setIntegration(data);
          broadcast(data);
        } else {
          cachedIntegration = null;
          setIntegration(null);
          broadcast(null);
        }
      } catch (err) {
        cachedIntegration = null;
        setIntegration(null);
        setError((err as Error)?.message ?? "Erro ao obter status Shopify.");
        broadcast(null);
      } finally {
        if (!options?.silent) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const listener = (data: ShopifyIntegrationStatus | null) => setIntegration(data);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    fetchStatus({ silent: true });
  }, [fetchStatus]);

  return { integration, loading, error, refresh: fetchStatus };
};

